import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationNgRxActions } from '@geekbears/gb-ngrx-auth-basic';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { ModalService, NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isNil } from 'lodash';
import { delay, filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';

import {
  ProfileActions,
  updatePasswordFailureAction,
  updatePasswordSuccessAction,
  updatePasswordLoadingSelector,
} from '@modules/regular-user/profile/application';
import { APP_ROUTES } from '@typings/app-routes.constant';

import { passwordValidator, getPasswordValidationMessages } from '@utils/password.validator';

@Component({
  selector: 'app-change-default-password',
  templateUrl: './change-default-password.page.html',
  styleUrls: ['./change-default-password.page.scss'],
})
export class ChangeDefaultPasswordComponent implements OnInit, OnDestroy {
  /**
   * @destroy$ is used to unsubscribe from all subscriptions
   */
  destroy$: Subject<void> = new Subject();
  form: FormGroup;
  updatePasswordLoading$: Observable<boolean | any>;

  constructor(
    private fb: FormBuilder,
    private store$: Store,
    private actions$: Actions,
    private router: Router,
    private modalService: ModalService,
    private notificationsService: NotificationsService
  ) {
    this.updatePasswordLoading$ = this.store$.select(
      updatePasswordLoadingSelector
    );
    this.form = this.fb.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required, passwordValidator()]],
    });
  }

  getPasswordErrorMessage(controlName: string): string {
    const control = this.form?.get(controlName);
    if (!control || !control.touched || control.valid) {
      return '';
    }
    if (control.errors?.['required']) {
      return 'Please enter your password';
    }
    if (control.errors?.['passwordStrength']?.firstMessage) {
      return control.errors['passwordStrength'].firstMessage;
    }
    const msgs = getPasswordValidationMessages(control.value || '');
    return msgs.length > 0 ? msgs[0] : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.onFailureActions();
    this.onPasswordUpdateSuccess();
  }

  onPasswordUpdateSuccess() {
    this.actions$
      .pipe(
        ofType(updatePasswordSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        tap(() => {
          this.store$.dispatch(
            AuthenticationNgRxActions.getAuthenticatedUserData()
          );
        }),
        delay(100),
        map((action) => {
          this.router.navigateByUrl(APP_ROUTES.dashboard.fullPath);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  updatePassword() {
    if (this.form.valid) {
      if (
        this.form.get('newPassword')?.value ==
        this.form.get('confirmPassword')?.value
      ) {
        this.store$.dispatch(
          ProfileActions.updatePassword({
            userData: {
              newPassword: this.form.get('newPassword')?.value,
              oldPassword: this.form.get('oldPassword')?.value,
            },
          })
        );
      } else {
        this.notificationsService.warning("The passwords doesn't match", {
          duration: 4000,
          icon: 'alert',
        });
      }
    }
  }

  onFailureActions() {
    this.actions$
      .pipe(
        ofType(updatePasswordFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            this.notificationsService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  back() {
    // localStorage.clear();
    localStorage.removeItem('s_admin');
    localStorage.removeItem('confirmAccountToken');
    localStorage.removeItem('confirmAccountEmail');
    this.store$.dispatch(AuthenticationNgRxActions.logout());
  }

  openModalExample() {
    // this.modalService
    //   .open(AuthModalsComponent, undefined, undefined, undefined, 'no-padding')
    //   .afterClosed()
    //   .subscribe(() => {
    //     window.location.reload();
    //   });
  }
}
